import { motion } from "framer-motion";
import { scenarios, type Scenario } from "@/data/showcase";
import { fadeUp, staggerSlow, viewportOnce } from "@/lib/motion";

const NON_APPROVED_LABELS: Record<string, { short: string; outcome: string }> = {
  obstructed: {
    short: "Łazienka zasłonięta przez ekipę",
    outcome: "Foton wycofał propozycję — czeka na czystsze zdjęcie",
  },
  "no-change": {
    short: "Bez zmian dwa dni z rzędu",
    outcome: "Foton sam ocenił, że nie ma o czym pisać",
  },
  reposition: {
    short: "Widok zupełnie inny niż wczoraj",
    outcome: "Operator sprawdził — to wyburzanie ściany",
  },
};

const APPROVED = scenarios.find((s) => s.kind === "approved")!;
const NON_APPROVED = scenarios.filter((s) => s.kind !== "approved");

export function Scenarios() {
  return (
    <section
      id="scenarios"
      className="relative bg-[#06080F] border-t border-ink-900 py-16 md:py-24 overflow-hidden"
    >
      <div className="relative mx-auto max-w-[1080px] px-6">
        <motion.div
          className="max-w-[760px] mb-12 md:mb-16"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-3">
            Cztery typowe dni
          </p>
          <h2 className="font-serif text-white leading-[1.05] text-3xl md:text-4xl lg:text-5xl max-w-[20ch]">
            Cztery dni z naszych kamer. Tylko jeden wymaga decyzji.
          </h2>
        </motion.div>

        <motion.div
          variants={staggerSlow}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          {/* Approved — editorial split: photos compact left, decision dominant right */}
          <motion.article variants={fadeUp} className="relative mb-16 md:mb-20">
            <span
              aria-hidden
              className="absolute -left-3 top-2 w-1.5 h-1.5 rounded-full bg-foton-light"
              style={{ boxShadow: "0 0 10px #8D51FF" }}
            />
            <div className="grid md:grid-cols-[1fr_1fr] gap-8 md:gap-12 items-start">
              {/* Left: photos compact + meta */}
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-3">
                  {APPROVED.cameraId} · {APPROVED.capturedBefore} → {APPROVED.capturedAfter}
                </p>
                <div className="grid grid-cols-2 gap-1.5 mb-3">
                  <img
                    src={APPROVED.imgBefore}
                    alt=""
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover"
                  />
                  <img
                    src={APPROVED.imgAfter}
                    alt=""
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover"
                  />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-foton-lighter">
                  ✦ klient zobaczył w panelu
                </p>
              </div>

              {/* Right: decision dominant */}
              <div>
                <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white leading-[1.1] mb-2">
                  {APPROVED.area.split(" · ")[1]}{" "}
                  <span className="text-ink-500">→</span>
                  <br />
                  {APPROVED.phase}
                </h3>
                <p className="font-serif text-6xl md:text-7xl lg:text-8xl text-foton-light num leading-none mt-6 mb-6">
                  {APPROVED.aiPickedDelta}
                </p>
                <p className="text-sm md:text-base text-ink-300 leading-relaxed max-w-[440px]">
                  {APPROVED.coordinatorVerdict}
                </p>
              </div>
            </div>
          </motion.article>

          {/* Non-approved — minimal photo-led row, no card chrome, no sigil glyphs */}
          <motion.p
            variants={fadeUp}
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-600 mb-6"
          >
            · trzy sytuacje, których nigdy nie widzisz w panelu ·
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {NON_APPROVED.map((s) => (
              <motion.article key={s.id} variants={fadeUp} className="space-y-4">
                <PhotoPair before={s.imgBefore} after={s.imgAfter} />
                <div>
                  <p className="font-serif text-base text-ink-100 leading-snug mb-2">
                    {NON_APPROVED_LABELS[s.id].short}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-600">
                    {s.cameraId}
                  </p>
                  <p className="text-xs text-ink-500 leading-snug mt-3">
                    {NON_APPROVED_LABELS[s.id].outcome}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function PhotoPair({ before, after }: { before: string; after: string }) {
  return (
    <div className="grid grid-cols-2 gap-1">
      <img
        src={before}
        alt=""
        loading="lazy"
        className="w-full aspect-[4/3] object-cover"
        style={{ filter: "grayscale(1)", opacity: 0.65 }}
      />
      <img
        src={after}
        alt=""
        loading="lazy"
        className="w-full aspect-[4/3] object-cover"
        style={{ filter: "grayscale(1)", opacity: 0.65 }}
      />
    </div>
  );
}

// Re-export type to keep tree-shake happy
export type { Scenario };
